import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { apiGet, useApi, formatPrice, type Listing } from '../lib/api';
import { getStripe } from '../lib/stripe';

type CreateOrderResponse = {
  order_id: string;
  client_secret: string;
  total_cents: number;
  fee_cents: number;
};

export default function CheckoutPage() {
  const { listingId } = useParams();
  // Demo/sample listings exist only client-side and can never be purchased.
  if (listingId?.startsWith('demo-')) {
    return (
      <p className="muted">
        This is a sample listing shown for testing — it can't be purchased. Turn off "Demo
        data" in the top bar to hide sample listings.
      </p>
    );
  }
  return <RealCheckout listingId={listingId} />;
}

function RealCheckout({ listingId }: { listingId?: string }) {
  const api = useApi();
  const [listing, setListing] = useState<Listing | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [order, setOrder] = useState<CreateOrderResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listingId) return;
    apiGet<{ listing: Listing }>(`/listings/${listingId}`).then(({ listing }) => setListing(listing));
  }, [listingId]);

  if (!listing) return <p className="muted">Loading…</p>;

  const remaining = listing.quantity_available - listing.quantity_sold;
  const total = listing.price_per_seat_cents * quantity;
  const seats: string[] = JSON.parse(listing.seats_json);

  async function startCheckout() {
    setCreating(true);
    setError(null);
    try {
      const res = await api<CreateOrderResponse>('/orders', {
        method: 'POST',
        body: JSON.stringify({ listing_id: listingId, quantity }),
      });
      setOrder(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'error');
    } finally {
      setCreating(false);
    }
  }

  return (
    <section>
      <h2>Checkout</h2>
      <div className="card">
        <p>
          <strong>{listing.opponent}</strong> — {listing.game_date}
        </p>
        <p>
          Section {listing.section} · Row {listing.row} · Seats {seats.join(', ')}
        </p>

        {!order && (
          <>
            <label>
              <span>Quantity</span>
              <select
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                disabled={creating}
              >
                {Array.from({ length: remaining }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <p className="price">Total: {formatPrice(total)}</p>
            <SignedOut>
              <p className="muted">Sign in to complete your purchase.</p>
              <SignInButton mode="modal" />
            </SignedOut>
            <SignedIn>
              <button onClick={startCheckout} disabled={creating}>
                {creating ? 'Preparing…' : 'Continue to payment'}
              </button>
            </SignedIn>
            {error && <p style={{ color: 'crimson' }}>{error}</p>}
          </>
        )}

        {order && <PaymentStep clientSecret={order.client_secret} orderId={order.order_id} />}
      </div>
    </section>
  );
}

function PaymentStep({ clientSecret, orderId }: { clientSecret: string; orderId: string }) {
  const stripePromise = useMemo(() => getStripe(), []);
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <PaymentForm orderId={orderId} />
    </Elements>
  );
}

function PaymentForm({ orderId }: { orderId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/orders/${orderId}?status=success`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'Payment failed');
      setSubmitting(false);
      return;
    }
    // Separate charges & transfers: the buyer is charged and the funds are held
    // by NYGTIX in escrow. The webhook flips the order to `paid_escrow`; route
    // the buyer to their orders page.
    navigate(`/orders/${orderId}?status=success`);
  }

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      <button style={{ marginTop: '1rem' }} disabled={!stripe || submitting}>
        {submitting ? 'Processing…' : 'Pay now'}
      </button>
      {error && <p style={{ color: 'crimson' }}>{error}</p>}
      <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
        Your payment is held securely by NYGTIX in escrow. We only release it to the seller after
        you confirm you received your tickets (or 48 h after transfer, automatically). If your
        tickets never arrive, you're refunded in full.
      </p>
    </form>
  );
}
